import { useCallback, useMemo, useState, type FC } from 'react'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'

import type { BannerProps } from '@wartek-id/banner-v1'
import { Banner, BannerAction, BannerDescription } from '@wartek-id/banner-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Link } from '@wartek-id/link'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast-v2'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Separator } from '@wartek-id/separator'

import { formatDateTimeWithTimezone } from 'utils/datetime'
import {
  API_GET_STATUS_PENGALIRAN_SKP,
  useApiGetPengaliranSkpStatus,
  useApiSyncSkpPerencanaan,
} from 'api/kinerja/pengaliran-data-ekin'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { TindakLanjut } from 'app/KinerjaShared/components/BannerPengaliranEkin/TindakLanjut'

import type { SyncSkpPerencanaanData } from 'types/kinerja-pengaliran'
import type {
  TRequestStatusPengaliranSkp,
  StatusPengaliranSKPSyncStatus,
} from 'api/kinerja/pengaliran-data-ekin'

type BannerPengaliranEkinProps = {
  jabatan: TRequestStatusPengaliranSkp['jabatan']
  hideRgtkColumn?: boolean
}

type TContent = Partial<BannerProps> & {
  show: boolean
  title?: string
  description?: string | React.ReactNode
  primaryAction?: React.ReactElement
  secondaryAction?: React.ReactElement
}

const validatePengaliranData = (
  data: any
): data is StatusPengaliranSKPSyncStatus => {
  return (
    data && typeof data === 'object' && 'synced' in data && 'lastSync' in data
  )
}

const JABATAN_LABEL = {
  ks: 'Kepala Sekolah',
  guru: 'Guru',
}
const BannerPengaliranEkin: FC<BannerPengaliranEkinProps> = ({
  jabatan,
  hideRgtkColumn = false,
}) => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { isEnabled, isFetched, periodYear } = usePeriodContext()

  // ------------------
  // Handle sync dialog
  // ------------------
  const [isDialogOpen, setDialogOpen] = useState(false)
  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSyncData(undefined)
  }
  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  // -------------------
  // Handle sync actions
  // -------------------
  const syncMutation = useApiSyncSkpPerencanaan()

  // We need to perceive the data until the next mutation is finished
  const [syncData, setSyncData] = useState<SyncSkpPerencanaanData | undefined>()
  const syncForce = syncData?.statusPengaliran?.force
  const isSyncSuccess = syncData?.statusPengaliran?.success

  const handleSyncAction = useCallback(
    (force: number) => {
      if (syncMutation.isLoading) return

      syncMutation.mutate(
        {
          year: periodYear,
          force: force ?? 0,
        },
        {
          onSuccess: (res) => {
            if (res?.meta?.success) {
              handleOpenDialog()

              queryClient.invalidateQueries(API_GET_STATUS_PENGALIRAN_SKP)

              setSyncData(res?.data)
            } else {
              handleCloseDialog()

              toast({
                message: 'Gagal melakukan sinkro perencanaan. Coba lagi.',
              })
            }
          },
          onError: () => {
            handleCloseDialog()

            toast({ message: 'Gagal melakukan sinkro perencanaan. Coba lagi.' })
          },
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodYear, syncMutation.isLoading]
  )

  // ---------------------------------
  // Resolve banner status and content
  // ---------------------------------
  const {
    data: { data } = {},
    isLoading,
    isSuccess,
  } = useApiGetPengaliranSkpStatus(
    { year: periodYear, jabatan },
    { enabled: isEnabled && isFetched }
  )

  const content: TContent = useMemo(() => {
    let res: TContent = { show: false }

    // If the data is not successfully fetched, or still fetching, return the default value
    if (!isSuccess || isLoading) return res

    // If the user is not ASN, return the default value
    if (!data.asn) return res

    const { pengaliran } = data
    if (!validatePengaliranData(pengaliran)) {
      // If the pengaliran data is not valid, return the default value
      return res
    }

    const { synced, success } = pengaliran

    const lastSync = pengaliran.lastSync

    if (synced) {
      if (success) {
        res = {
          primaryAction: undefined,
          secondaryAction: <LinkEKinerja />,
          className: '!bg-green-5 !border-green-50',
          customIcon: (
            <Icon color="default" fontSize="default">
              check_circle_outline
            </Icon>
          ),
          description: (
            <div>
              <Text>
                Kunjungi E-Kinerja untuk cek data. Jika ada perbedaan, tekan
                tombol "Sinkro Ruang GTK" di E-Kinerja dan ikuti petunjuk yang
                tersedia.
              </Text>

              <br />
              <Text variant="body-sm">
                Status sinkro data E-Kinerja per:{' '}
                {formatDateTimeWithTimezone({ dateTime: lastSync })}
              </Text>
            </div>
          ),
          show: true,
          title: 'Data Perencanaan (SKP) Anda berhasil dialirkan ke E-Kinerja',
          type: 'informational',
        }
      } else {
        res = {
          secondaryAction: <LinkEKinerja />,
          primaryAction: (
            <ButtonSync
              label="Coba lagi"
              isLoading={syncMutation.isLoading}
              onClick={() => handleSyncAction(0)}
            />
          ),
          customIcon: (
            <Icon color="default" fontSize="default">
              warning
            </Icon>
          ),
          description: (
            <div>
              <Text>
                Alasan: {pengaliran.message}. Mohon coba lagi atau Anda dapat
                juga menekan tombol "Sinkro Ruang GTK" di E-Kinerja dan ikuti
                petunjuk yang tersedia.
              </Text>
              <br />
              <Text variant="body-sm">
                Status sinkro data E-Kinerja per:{' '}
                {formatDateTimeWithTimezone({ dateTime: lastSync })}
              </Text>
            </div>
          ),
          show: true,
          title: 'Data Perencanaan (SKP) Anda gagal dialirkan ke E-Kinerja',
          type: 'critical',
        }
      }
    } else {
      res = {
        secondaryAction: <LinkEKinerja />,
        primaryAction: (
          <ButtonSync
            label="Kirim"
            isLoading={syncMutation.isLoading}
            onClick={() => handleSyncAction(0)}
          />
        ),
        className: '!bg-blue-5 !border-blue-70',
        customIcon: (
          <Icon
            className="banner-title-icon !mr-3"
            color="default"
            fontSize="default"
          >
            info_outline
          </Icon>
        ),
        description:
          'Anda dapat juga menekan tombol "Sinkro Ruang GTK" di E-Kinerja dan ikuti petunjuk yang tersedia.',
        show: true,
        title: 'Kirim Data Perencanaan (SKP) Anda ke E-Kinerja',
        type: 'informational',
      }
    }

    return res
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isSuccess, syncMutation.isLoading])

  if (isLoading || !content.show) return null

  return (
    <>
      <Banner
        role="alert"
        closable={false}
        customIcon={content?.customIcon}
        title={content?.title}
        type={content?.type}
        className={clsx(
          '!p-3',
          '[&>div:nth-child(1)>div:nth-child(1):before]:hidden', // Hide predefined icon
          '[&>div:nth-child(1)>div]:space-x-3', // Extra spacing between icon and title
          '[&>div:nth-child(1)>div>span[role=img]]:m-0', // Remove extra margin on icon
          '[&>div:nth-child(2)]:mt-1', // Override margin between title and description
          '[&>div:nth-child(2)]:pl-9', // Override padding for description
          '[&>div:nth-child(2)>div:nth-child(2)]:mt-1', // Override margin between description and action
          content?.className
        )}
      >
        {content?.description && (
          <BannerDescription>
            <Text>{content?.description}</Text>
          </BannerDescription>
        )}

        <BannerAction className="!mt-4 flex items-center gap-3">
          {content.primaryAction}
          {content.secondaryAction}
        </BannerAction>
      </Banner>

      <Dialog isOpen={isDialogOpen} onClose={handleCloseDialog}>
        <DialogOverlay />
        <DialogContent>
          <DialogCloseButton />
          <DialogHeader
            title="Sinkro Data Ruang GTK"
            className="bg-surface-subdued"
          />
          <section className="p-6 flex flex-col">
            <div className="flex flex-col gap-3">
              <Text variant="action" as="h3">
                Data Pegawai
              </Text>
              <dl className="flex gap-9">
                <div className="flex flex-col gap-1">
                  <Text color="subdued" variant="helper" as="dt">
                    Nama di E-Kinerja
                  </Text>
                  <Text variant="body-sm-bold" as="dd">
                    {syncData?.pegawaiEkin?.namaPegawai || '-'}
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text color="subdued" variant="helper" as="dt">
                    Nama di Ruang GTK
                  </Text>
                  <Text variant="body-sm-bold" as="dd">
                    {syncData?.pegawai?.namaPegawai || '-'}
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text color="subdued" variant="helper" as="dt">
                    NIP
                  </Text>
                  <Text variant="body-sm-bold" as="dd">
                    {syncData?.pegawai?.namaPegawai || '-'}
                  </Text>
                </div>
              </dl>
            </div>

            <Separator className="my-5" />

            <div className="flex flex-col gap-3">
              <Text variant="action" as="h3">
                Data SKP
              </Text>
              <dl className="flex gap-9">
                <div className="flex flex-col gap-1">
                  <Text color="subdued" variant="helper" as="dt">
                    Periode Awal
                  </Text>
                  <Text variant="body-sm-bold" as="dd">
                    <time
                      dateTime={`${periodYear}-01-01`}
                    >{`${periodYear}-01-01`}</time>
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text color="subdued" variant="helper" as="dt">
                    Periode Akhir
                  </Text>
                  <Text variant="body-sm-bold" as="dd">
                    <time
                      dateTime={`${periodYear}-12-31`}
                    >{`${periodYear}-12-31`}</time>
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text color="subdued" variant="helper" as="dt">
                    Pendekatan
                  </Text>
                  <Text variant="body-sm-bold" as="dd">
                    Kuantitatif
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text color="subdued" variant="helper" as="dt">
                    Jenis SKP
                  </Text>
                  <Text variant="body-sm-bold" as="dd">
                    {JABATAN_LABEL[jabatan] || '-'}
                  </Text>
                </div>
              </dl>
            </div>

            <table className="mt-8 mb-6 w-full text-xs border border-b-0 border-subdued border-separate border-spacing-0 rounded-2xl">
              <thead className="bg-surface-subdued">
                <tr>
                  <th className="text-left p-4 border-b border-subdued rounded-tl-2xl" />
                  <th className="text-left p-4 border-b border-subdued" />
                  <th className="text-left p-4 border-b border-subdued">
                    Data E-Kinerja
                  </th>
                  {!hideRgtkColumn && (
                    <th className="text-left p-4 border-b border-subdued rounded-tr-2xl">
                      Data Ruang GTK
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-4 border-r border-b border-subdued font-bold">
                    Pegawai
                  </td>
                  <td className="p-4 border-b border-subdued font-bold">
                    Unor
                  </td>
                  <td className="p-4 border-b border-subduedo verflow-wrap-anywhere">
                    {syncData?.pegawaiEkin?.pegawaiUnor || '-'}
                  </td>
                  {!hideRgtkColumn && (
                    <td className="p-4 border-b border-subdued overflow-wrap-anywhere">
                      {syncData?.pegawai?.pegawaiUnor || '-'}
                    </td>
                  )}
                </tr>
                <tr>
                  <td
                    className="p-4 border-r border-b border-subdued rounded-bl-2xl font-bold"
                    rowSpan={3}
                  >
                    Atasan
                  </td>
                  <td className="p-4 border-b border-subdued font-bold">
                    Nama
                  </td>
                  <td className="p-4 border-b border-subdued overflow-wrap-anywhere">
                    {syncData?.pegawaiEkin?.pegawaiAtasanData?.nama || '-'}
                  </td>
                  {!hideRgtkColumn && (
                    <td className="p-4 border-b border-subdued overflow-wrap-anywhere">
                      {syncData?.pegawai?.pegawaiAtasanData?.nama || '-'}
                    </td>
                  )}
                </tr>
                <tr>
                  <td className="p-4 border-b border-subdued font-bold">NIP</td>
                  <td className="p-4 border-b border-subdued overflow-wrap-anywhere">
                    {syncData?.pegawaiEkin?.pegawaiAtasanData?.nip || '-'}
                  </td>
                  {!hideRgtkColumn && (
                    <td className="p-4 border-b border-subdued overflow-wrap-anywhere">
                      {syncData?.pegawai?.pegawaiAtasanData?.nip || '-'}
                    </td>
                  )}
                </tr>
                <tr>
                  <td className="p-4 border-b border-subdued font-bold">
                    Unor
                  </td>
                  <td className="p-4 border-b border-subdued overflow-wrap-anywhere">
                    {syncData?.pegawaiEkin?.pegawaiAtasanData?.unor || '-'}
                  </td>
                  {!hideRgtkColumn && (
                    <td className="p-4 border-b border-subdued rounded-br-2xl overflow-wrap-anywhere">
                      {syncData?.pegawai?.pegawaiAtasanData?.unor || '-'}
                    </td>
                  )}
                </tr>
              </tbody>
            </table>

            {/* Successful sync */}
            {isSyncSuccess && (
              <div className="rounded-2xl border border-subdued">
                <div
                  className={
                    'p-4 rounded-tl-2xl rounded-tr-2xl flex gap-2 bg-surface-success-subdued'
                  }
                >
                  <Icon color="success">check_circle</Icon>
                  <Text variant="heading-sm">
                    Berhasil melakukan Sinkro dengan data:
                  </Text>
                </div>

                <div className="p-4">
                  <Text variant="body-sm-bold" className="mb-3">
                    RHK Individu
                  </Text>
                  <ul className="list-disc pl-4">
                    <li className="mb-3">
                      <Text variant="body-sm">
                        {`RHK Masuk: ${syncData?.statusPengaliran?.rhkMasuk}`}
                      </Text>
                    </li>
                    <li>
                      <Text variant="body-sm">
                        {`RHK Indikator Masuk: ${syncData?.statusPengaliran?.rhkIndikatorMasuk}`}
                      </Text>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Failed sync */}
            {!isSyncSuccess && (
              <>
                {syncForce > 1 && (
                  <div className="rounded-2xl border border-subdued">
                    <div className="p-4 rounded-tl-2xl rounded-tr-2xl flex gap-2 bg-surface-critical-subdued">
                      <Icon color="critical">warning</Icon>
                      <Text variant="heading-sm">
                        Gagal melakukan Sinkro dikarenakan
                      </Text>
                    </div>
                    <div className="p-4">
                      <Text variant="body-sm" className="mb-4">
                        {syncData?.statusPengaliran?.errorLog?.[0] || '-'}
                      </Text>
                      <Text variant="body-sm-bold" className="mb-1">
                        Saran tindak lanjut:
                      </Text>
                      <TindakLanjut
                        errorMessage={
                          syncData?.statusPengaliran?.errorLog?.[0] ?? ''
                        }
                      />
                    </div>
                  </div>
                )}

                <div className="rounded-2xl border border-subdued p-4 mt-4">
                  <Text variant="action" className="mb-2">
                    Terdapat catatan saat sinkro:
                  </Text>
                  <Text variant="body-sm">
                    {syncData?.statusPengaliran?.catatan?.[0] || '-'}
                  </Text>
                </div>
              </>
            )}
          </section>

          <DialogFooter className="flex flex-col">
            {!isSyncSuccess && syncForce === 1 && (
              <div className="flex-1 mb-4 bg-surface-informational-subdued p-4 rounded-lg flex gap-2">
                <Icon color="informational">info</Icon>
                <Text variant="body-sm">
                  Mohon pastikan pegawai sudah sesuai penempatan unit
                  organisasinya di e-Kinerja dengan Ruang GTK, agar tidak
                  terjadi kesalahan data.
                </Text>
              </div>
            )}

            <div className="flex gap-4">
              {isSyncSuccess ? (
                <Button className="flex-1" onClick={handleCloseDialog}>
                  Selesai
                </Button>
              ) : (
                <>
                  <Button
                    color="white"
                    className="flex-1"
                    disabled={syncMutation.isLoading}
                    onClick={handleCloseDialog}
                  >
                    Kembali
                  </Button>
                  {syncForce === 1 && (
                    <Button
                      className="flex-1"
                      isLoading={syncMutation.isLoading}
                      onClick={() => handleSyncAction(1)}
                    >
                      Tetap lakukan Sinkro
                    </Button>
                  )}
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default BannerPengaliranEkin

function LinkEKinerja() {
  return (
    <Link
      href="https://kinerja.bkn.go.id/skp"
      target="_blank"
      variant="primary"
      className="!no-underline hover:!underline !p-0 focus:!shadow-none !text-blue-70 font-bold"
    >
      Ke E-Kinerja
    </Link>
  )
}

type ButtonSyncProps = {
  label: string
  isLoading?: boolean
  onClick: () => void
}
function ButtonSync({ isLoading, label, onClick }: ButtonSyncProps) {
  return (
    <Button
      color="black"
      variant="solid"
      size="sm"
      className="!bg-gray-90"
      isLoading={isLoading}
      onClick={onClick}
    >
      {label}
    </Button>
  )
}
